import { NavLink as RouterNavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Building2, Users, CreditCard, BarChart3, MapPinned,
  Bot, Bell, ScrollText, Settings, UserCircle2, LogOut, ShieldCheck,
} from "lucide-react";
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel,
  SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarFooter, SidebarHeader, useSidebar,
} from "@/components/ui/sidebar";
import { useAuth } from "@/auth/AuthContext";
import { cn } from "@/lib/utils";

const platformItems = [
  { title: "Platform Dashboard", url: "/platform", icon: LayoutDashboard, end: true },
  { title: "Tenants", url: "/platform/tenants", icon: Building2 },
  { title: "Subscriptions & Plans", url: "/platform/subscriptions", icon: CreditCard },
  { title: "Sites Overview", url: "/platform/sites", icon: MapPinned },
];

const governanceItems = [
  { title: "AI Assistant Control", url: "/platform/ai-control", icon: Bot },
  { title: "Global Notifications", url: "/platform/notifications", icon: Bell },
  { title: "Audit Logs", url: "/platform/audit", icon: ScrollText },
  { title: "Platform Settings", url: "/platform/settings", icon: Settings },
  { title: "Profile", url: "/platform/profile", icon: UserCircle2 },
];

export function PlatformSidebar() {
  const { state } = useSidebar();
  const { user, logout } = useAuth();
  const collapsed = state === "collapsed";
  const location = useLocation();

  const renderItem = (item: typeof platformItems[number]) => {
    const isActive = item.end ? location.pathname === item.url : location.pathname.startsWith(item.url);
    return (
      <SidebarMenuItem key={item.title}>
        <SidebarMenuButton asChild tooltip={item.title} isActive={isActive}>
          <RouterNavLink
            to={item.url}
            end={item.end}
            className={cn(
              "group flex items-center gap-3 rounded-md px-2.5 py-2 text-sm transition-colors",
              isActive
                ? "bg-accent/20 text-white font-medium"
                : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-white",
            )}
          >
            <item.icon className={cn("h-4 w-4 shrink-0", isActive && "text-accent")} />
            {!collapsed && <span className="truncate">{item.title}</span>}
            {isActive && !collapsed && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-accent" />}
          </RouterNavLink>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border">
      <SidebarHeader className="border-b border-sidebar-border bg-sidebar px-3 py-4">
        {collapsed ? (
          <div className="flex justify-center">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-accent to-primary">
              <ShieldCheck className="h-5 w-5 text-white" />
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2.5">
            <div className="relative flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-accent to-primary shadow-glow">
              <ShieldCheck className="h-5 w-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="leading-tight">
              <div className="font-display text-base font-bold tracking-tight text-white">GeoKerja</div>
              <div className="text-[10px] font-medium uppercase tracking-wider text-accent">Platform Console</div>
            </div>
          </div>
        )}
      </SidebarHeader>

      <SidebarContent className="bg-sidebar px-2 py-3">
        <SidebarGroup>
          {!collapsed && <SidebarGroupLabel className="text-[10px] uppercase tracking-wider text-sidebar-foreground/50">Platform</SidebarGroupLabel>}
          <SidebarGroupContent>
            <SidebarMenu>{platformItems.map(renderItem)}</SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          {!collapsed && <SidebarGroupLabel className="text-[10px] uppercase tracking-wider text-sidebar-foreground/50">Governance</SidebarGroupLabel>}
          <SidebarGroupContent>
            <SidebarMenu>{governanceItems.map(renderItem)}</SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border bg-sidebar p-3">
        {!collapsed ? (
          <div className="flex items-center gap-3 rounded-lg p-2 hover:bg-sidebar-accent transition-colors">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-accent to-primary text-xs font-semibold text-white">
              {user?.avatarInitials ?? "SA"}
            </div>
            <div className="min-w-0 flex-1 leading-tight">
              <div className="truncate text-xs font-medium text-white">{user?.name}</div>
              <div className="truncate text-[10px] text-accent">Super Admin</div>
            </div>
            <button onClick={logout} className="rounded p-1.5 text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-white transition-colors" title="Logout">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <button onClick={logout} className="mx-auto flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-accent to-primary text-xs font-semibold text-white" title="Logout">
            {user?.avatarInitials ?? "SA"}
          </button>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
